import { Button, InputLabel, Select } from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import InputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import HeaderPage from '~/components/HeaderPage';
import { ApplicationState } from '~/store';
import { AuthState } from '~/store/ducks/auth/types';
import {
  assuntoContactRequest,
  contactDPORequest,
} from '~/store/ducks/ContactDPOLogado/actions';
import { ThemeState } from '~/store/ducks/theme/types';
import getValidationErrors from '../../utils/getValidationErrors';
import {
  Container,
  FormControlLGPD,
  MessageDPO,
  ParagraphDPO,
  StyledButton,
  StyledTextField,
} from './styles';

interface FormContact {
  identification_key: string;
  email: string;
  emailConfirmation: string;
}

const ContactDPOLogado: React.FC = () => {
  const _reCaptchaRef = useRef<ReCAPTCHA>(null);

  const dispatch = useDispatch();

  const assuntos = useSelector<ApplicationState, any>(
    state => state.ContactDPOLogado.data,
  );

  const theme = useSelector<ApplicationState, ThemeState>(state => state.theme);

  const [form, setForm] = useState<FormContact>({
    identification_key: '',
    email: '',
    emailConfirmation: '',
  });

  const [ownership, setOwnership] = useState<number | null>(0);
  const [assunto, setAssunto] = useState<number | undefined>(0);
  const [message, setMessage] = useState<string | undefined>('');
  const [formErr, setFormErr] = useState<any>({});

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.target;
      const name = target.name;

      setForm({
        ...form,
        [name]: target.value,
      });

      setFormErr({
        ...formErr,
        [name]: false,
      });
    },
    [form, formErr],
  );

  const [isVerified, setIsVerified] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const [recaptchaTheme, setRecaptchaTheme] = useState(true);

  const [files, setFiles] = useState<File[]>([]);

  const handleChangefile = (files: File[]) => {
    setFiles(files);
    setFormErr({
      ...formErr,
      file: false,
    });
  };

  const handleChangeOwnership = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: any;
    }>,
  ) => {
    setOwnership(event.target.value);
    setFormErr({
      ...formErr,
      ownership_type: false,
    });
  };

  const handleChangeAssunto = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: any;
    }>,
  ) => {
    setAssunto(event.target.value);
    setFormErr({
      ...formErr,
      subject: false,
    });
  };

  const handleChangeMessageDPO = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setMessage(event.target.value);
    setFormErr({
      ...formErr,
      content: false,
    });
  };

  const auth = useSelector<ApplicationState, AuthState>(state => state.auth);
  const common = useSelector<ApplicationState, any>(state => state.common);

  const contact = useCallback(async () => {
    const formContact = {
      ...form,
      ownership_type: ownership,
      subject: assunto,
      content: message?.trim(),
      country_company: auth.company.country_company_id,
      file: files,
      isVerified,
      recaptcha,
    };

    try {
      const schema = Yup.object().shape({
        identification_key: Yup.string().required('CPF obrigatório'),
        ownership_type: Yup.string().test(
          'ownership_type',
          'Categoria obrigatório',
          value => {
            return value != 0 ? true : false;
          },
        ),
        email: Yup.string()
          .required('E-mail obrigatório')
          .email('Digite um e-mail válido'),
        emailConfirmation: Yup.string()
          .required('Confirmação de email obrigatório')
          .oneOf([Yup.ref('email')], 'Confirmação de email incorreta'),
        subject: Yup.string().test('subject', 'Assunto obrigatório', value => {
          return value !== 0 ? true : false;
        }),
        file: Yup.string().test('file', 'file obrigatório', value => {
          return value.length !== 0 ? true : false;
        }),
        content: Yup.string().min(5).required('Menssagem obrigatório'),
        isVerified: Yup.bool().oneOf([true], 'Por favor preencha o reCAPTCHA'),
      });

      await schema.validate(formContact, { abortEarly: false });

      dispatch(contactDPORequest(formContact));

      _reCaptchaRef.current?.reset();
      setIsVerified(false);
      setRecaptcha('');

      setForm({ identification_key: '', email: '', emailConfirmation: '' });
      setOwnership(0);
      setAssunto(0);
      setMessage('');
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const err = getValidationErrors(error);

        let {
          content,
          email,
          emailConfirmation,
          identification_key,
          ownership_type,
          subject,
        } = err;

        if (!!email || !!identification_key || !!ownership_type || !!subject) {
          toast.error(
            'Necessário o preenchimento dos campos de identificação.',
          );
        }

        if (
          !!emailConfirmation &&
          !!content &&
          !email &&
          !identification_key &&
          !ownership_type &&
          !subject
        ) {
          toast.error(
            'Necessário o preenchimento dos campos de identificação.',
          );
        }

        if (!!err.file) {
          toast.error('Obrigatório um documento com foto anexado.');
        }

        const isValidEmailConfirmation =
          !content &&
          !email &&
          !identification_key &&
          !ownership_type &&
          !subject;

        if (!!emailConfirmation && isValidEmailConfirmation) {
          toast.error(
            'O conteúdo dos campos Endereço de E-mail e Redigite o endereço de e-mail deve ser igual.',
          );
        }

        const isValidContent =
          !emailConfirmation &&
          !email &&
          !identification_key &&
          !ownership_type &&
          !subject;

        if (!!content && isValidContent) {
          toast.error('Favor preencher a sua solicitação.');
        }

        if (!isVerified) {
          toast.error('Por favor, preencha o reCAPTCHA');
        }

        setFormErr(err);

        _reCaptchaRef.current?.reset();
        setIsVerified(false);
        setRecaptcha('');

        return;
      }
    }
  }, [
    assunto,
    auth.company.country_company_id,
    dispatch,
    files,
    form,
    isVerified,
    message,
    ownership,
    recaptcha,
  ]);

  useEffect(() => {
    document.title = 'Solicitar ajuda ao DPO | Portal LGPD';
    dispatch(assuntoContactRequest(auth.company?.country_company_id));
  }, [auth.company, dispatch]);

  useEffect(() => {
    setTimeout(() => setRecaptchaTheme(state => !state), 10);
    setTimeout(() => setRecaptchaTheme(state => !state), 20);

    _reCaptchaRef.current?.reset();
  }, [theme.theme]);

  const verifyCallback = (token: string | null) => {
    if (token) {
      setIsVerified(true);
      setRecaptcha(token);
    }
  };

  const expiredCallback = () => {
    setIsVerified(false);
    setRecaptcha('');
  };

  return (
    <Container>
      <HeaderPage pageTitle="Solicitar ajuda ao DPO" />

      <div className="main-content-solicitar-ajuda-dpo">
        <div className="contact-dpo-left">
          <InputMask
            mask="999.999.999-99"
            value={form.identification_key}
            onChange={handleChange}
            name="identification_key"
            disabled={false}
            maskChar=""
            children={() => (
              <StyledTextField
                identificationkeyerror={
                  !!formErr?.identification_key ? true : false
                }
                id="identificationKey"
                name="identification_key"
                label="CPF"
                variant="outlined"
              />
            )}
          />

          <FormControlLGPD
            variant="outlined"
            ownershiptypeerror={!!formErr?.ownership_type ? true : false}
          >
            <InputLabel htmlFor="ownershipType">Categoria</InputLabel>
            <Select
              native
              value={ownership}
              onChange={handleChangeOwnership}
              label="Categoria"
              inputProps={{
                name: 'ownershipType',
                id: 'ownershipType',
              }}
            >
              <option aria-label="ownershipType" value="" />
              {!!common.categoria[0] &&
                common.categoria?.map((el: any) => {
                  return (
                    <option
                      key={el.data_holder_cat_id}
                      value={el.data_holder_cat_id}
                    >
                      {el.data_holder_cat_name}
                    </option>
                  );
                })}
            </Select>
          </FormControlLGPD>
          <StyledTextField
            identificationkeyerror={
              !!formErr?.email || !!formErr?.emailConfirmation ? true : false
            }
            id="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            label="Endereço de E-mail"
            variant="outlined"
            className="email-field"
          />
          <StyledTextField
            identificationkeyerror={!!formErr?.emailConfirmation ? true : false}
            id="email-confirmation"
            name="emailConfirmation"
            value={form.emailConfirmation}
            onChange={handleChange}
            label="Redigite o endereço de E-mail"
            variant="outlined"
          />
        </div>

        <div className="contact-dpo-right">
          <FormControlLGPD
            variant="outlined"
            ownershiptypeerror={!!formErr?.subject ? true : false}
          >
            <InputLabel htmlFor="assunto">Assunto</InputLabel>
            <Select
              native
              value={assunto}
              //@ts-ignore
              onChange={handleChangeAssunto}
              label="Assunto"
              inputProps={{
                name: 'assunto',
                id: 'assunto',
              }}
            >
              <option aria-label="assunto" value="" />
              {assuntos[0] &&
                assuntos.map((el: any, i: any) => (
                  <option key={i} value={el.contact_subject_id}>
                    {el.subject_ds}
                  </option>
                ))}
            </Select>
          </FormControlLGPD>

          <div
            style={{
              border: `${!!formErr.file ? '2px dashed #c53030' : 'none'}`,
              marginTop: '29px',
            }}
          >
            <DropzoneArea
              dropzoneClass="input-file-LGPD"
              onChange={handleChangefile}
              maxFileSize={5000000}
              dropzoneText={
                files[0]?.name
                  ? files.length > 1
                    ? `${files.length} arquivos selecionados (limite de 10 arquivos e 5 MB para cada arquivo)`
                    : `${files.length} arquivos selecionado (limite de 10 arquivos e 5 MB para cada arquivo)`
                  : 'Selecione um ou mais arquivos (limite de 10 arquivos e 5 MB para cada arquivo)'
              }
              showFileNames
              filesLimit={10}
              getFileAddedMessage={fileName =>
                `Arquivo ${fileName} adicionado com sucesso. ${' '}`
              }
              getFileLimitExceedMessage={filesLimit =>
                `Maximo 10 arquivo no formato de imagem ou pdf com limite de 5 MB é permitido. ${' '}`
              }
              getDropRejectMessage={(
                rejectedFile,
                acceptedFiles,
                maxFileSize,
              ) =>
                `Maximo 10 arquivo no formato de imagem ou pdf com limite de 5 MB é permitido. ${' '}`
              }
              getFileRemovedMessage={fileName =>
                `Arquivo ${fileName} removido. ${' '}`
              }
              clearOnUnmount
            />
          </div>

          <ParagraphDPO
            className="descricao-solicitacao"
            messageError={!!formErr.content ? true : false}
          >
            Descrição da solicitação
          </ParagraphDPO>

          <MessageDPO
            messageError={!!formErr.content ? true : false}
            name="message"
            id="message"
            value={message}
            onChange={handleChangeMessageDPO}
            rows={10}
            className="message-DPO"
          />
        </div>
      </div>
      {process.env.REACT_APP_RECAPTCHA_SITE_KEY && recaptchaTheme ? (
        <ReCAPTCHA
          //@ts-ignore
          style={{ display: 'inline-block', marginBottom: 40, marginTop: 20 }}
          theme={theme.theme}
          ref={_reCaptchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={verifyCallback}
          onExpired={expiredCallback}
        />
      ) : (
        ''
      )}

      <div className="buttonGroup">
        <Button
          onClick={() => {
            const toastrConfirmOptions = {
              onOk: () => {},
              onCancel: () => {},
              okText: 'Ok',
              disableCancel: true,
            };

            toastr.confirm(
              'As informações previstas no artigo 18 da Lei Geral de Proteção de Dados estão no Portal LGPD da SKY. Neste Portal, você pode visualizar e gerenciar seus dados e exercer seus direitos. Caso tenha dúvidas ou outras solicitações, pode solicitar ajuda ao DPO. Neste caso, preencha os dados no formulário desta funcionalidade e faça a sua solicitação; ela será respondida por e-mail em até 15 dias.',
              toastrConfirmOptions,
            );
          }}
          variant="outlined"
          className="button-continue ajuda"
        >
          Ajuda
        </Button>

        <StyledButton onClick={contact} className="button-continue">
          Enviar
        </StyledButton>
      </div>
    </Container>
  );
};

export default ContactDPOLogado;
